import { apiClient } from '@/lib/api-client';
import type {
  ApiResponse,
  PaginatedResponse,
  Property,
  CreatePropertyRequest,
  UpdatePropertyRequest,
} from '@/types/api';

export type PropertySortBy =
  | 'name'
  | 'buildingCount'
  | 'unitCount'
  | 'deviceCount'
  | 'activeAlarmCount'
  | 'createdAt';
export type PropertySortDir = 'asc' | 'desc';

export interface GetPropertiesParams {
  search?: string;
  type?: string;
  sortBy?: PropertySortBy;
  sortDir?: PropertySortDir;
  page?: number;
  perPage?: number;
}

export async function getProperties(params: GetPropertiesParams = {}): Promise<PaginatedResponse<Property>> {
  const query = new URLSearchParams();
  if (params.search) query.set('search', params.search);
  if (params.type) query.set('type', params.type);
  if (params.sortBy) query.set('sortBy', params.sortBy);
  if (params.sortDir) query.set('sortDir', params.sortDir);
  if (params.page) query.set('page', String(params.page));
  if (params.perPage) query.set('perPage', String(params.perPage));

  const response = await apiClient.get<PaginatedResponse<Property>>(`/properties?${query.toString()}`);
  return response.data;
}

export async function getProperty(id: string): Promise<ApiResponse<Property>> {
  const response = await apiClient.get<ApiResponse<Property>>(`/properties/${id}`);
  return response.data;
}

export async function createProperty(data: CreatePropertyRequest): Promise<ApiResponse<Property>> {
  const response = await apiClient.post<ApiResponse<Property>>('/properties', data);
  return response.data;
}

export async function updateProperty(id: string, data: UpdatePropertyRequest): Promise<ApiResponse<null>> {
  const response = await apiClient.put<ApiResponse<null>>(`/properties/${id}`, data);
  return response.data;
}

export async function deleteProperty(id: string): Promise<ApiResponse<null>> {
  const response = await apiClient.delete<ApiResponse<null>>(`/properties/${id}`);
  return response.data;
}

export interface ReassignOrganizationResult {
  propertyId: string;
  sourceOrganizationId: string;
  targetOrganizationId: string;
  buildingsAffected: number;
  unitsAffected: number;
  devicesAffected: number;
  gatewaysAffected: number;
  reportsAffected: number;
}

export async function reassignPropertyOrganization(
  propertyId: string,
  targetOrganizationId: string,
): Promise<ApiResponse<ReassignOrganizationResult>> {
  const response = await apiClient.post<ApiResponse<ReassignOrganizationResult>>(
    `/properties/${propertyId}/reassign-organization`,
    { targetOrganizationId },
  );
  return response.data;
}
